/**
 * Helper utilities for LiveKit configuration management
 */

import type { JsonObject } from "@/types/json"

interface LiveKitMetadata {
  generate_token_endpoint?: string
  generate_token_headers?: string | JsonObject
  token_request_payload?: string | JsonObject
  content_type?: string
  livekit_url?: string
  livekit_agent_name?: string
}

/**
 * Parse the existing generate_token_headers value (string or object — the
 * advanced Custom Headers JSON editor can leave it as either) into an object,
 * tolerating invalid/absent JSON by falling back to an empty object.
 */
function parseExistingHeaders(
  metadata: Record<string, unknown>,
): Record<string, string> {
  const liveKitData = metadata as LiveKitMetadata
  if (!liveKitData.generate_token_headers) {
    return {}
  }
  try {
    const headers =
      typeof liveKitData.generate_token_headers === "string"
        ? (JSON.parse(liveKitData.generate_token_headers) as unknown)
        : liveKitData.generate_token_headers
    return headers && typeof headers === "object"
      ? (headers as Record<string, string>)
      : {}
  } catch {
    return {}
  }
}

/**
 * Extract individual header values from LiveKit metadata
 */
export const getLiveKitHeaderValue = (
  metadata: Record<string, unknown>,
  headerName: string,
): string => {
  const liveKitData = metadata as LiveKitMetadata

  // First check if we have a separate field
  if (headerName === "Content-Type" && liveKitData.content_type) {
    return liveKitData.content_type
  }

  // Fallback to parsing from existing generate_token_headers
  return parseExistingHeaders(metadata)[headerName] || ""
}

/**
 * Merge a single header key/value into whatever generate_token_headers
 * already holds (e.g. a manually-set Authorization header from the Custom
 * Headers advanced JSON editor), preserving every other key instead of
 * clobbering the whole object. Shared primitive behind the Content-Type and
 * Sandbox ID setters below.
 */
function mergeLiveKitHeader(
  metadata: Record<string, unknown>,
  key: string,
  value: string,
): Record<string, string> {
  return {
    ...parseExistingHeaders(metadata),
    [key]: value,
  }
}

/**
 * Create a metadata update that sets the Content-Type header, merging it
 * non-destructively into any existing generate_token_headers.
 */
export const createLiveKitContentTypeUpdate = (
  metadata: Record<string, unknown>,
  contentType: string,
): Record<string, unknown> => {
  return {
    content_type: contentType,
    generate_token_headers: mergeLiveKitHeader(
      metadata,
      "Content-Type",
      contentType,
    ), // Store as object for backend
  }
}

/**
 * Create a metadata update that sets the X-Sandbox-ID header, merging it
 * non-destructively into any existing generate_token_headers. This header is
 * only relevant when pointing generate_token_endpoint at LiveKit's own hosted
 * Sandbox / Development Token Server API — it identifies which LiveKit
 * project/sandbox to mint a token from. There is no separate top-level
 * sandbox_id metadata field (the backend's old one was dead code and has
 * been removed for good); the header is the only place this value lives.
 */
export const createLiveKitSandboxIdUpdate = (
  metadata: Record<string, unknown>,
  sandboxId: string,
): Record<string, unknown> => {
  return {
    generate_token_headers: mergeLiveKitHeader(
      metadata,
      "X-Sandbox-ID",
      sandboxId,
    ),
  }
}

/**
 * Get current content type from metadata
 */
export const getCurrentContentType = (
  metadata: Record<string, unknown>,
): string => {
  return getLiveKitHeaderValue(metadata, "Content-Type") || "application/json"
}

/**
 * Get current sandbox ID from metadata, read from the X-Sandbox-ID header
 * (there is no separate top-level field — see createLiveKitSandboxIdUpdate).
 */
export const getCurrentSandboxId = (
  metadata: Record<string, unknown>,
): string => {
  return getLiveKitHeaderValue(metadata, "X-Sandbox-ID")
}

/**
 * Get custom headers as string (for editing)
 */
export const getCustomHeaders = (metadata: Record<string, unknown>): string => {
  const liveKitData = metadata as LiveKitMetadata
  if (!liveKitData.generate_token_headers) {
    return ""
  }
  // Return as-is if string, stringify if object
  const value = liveKitData.generate_token_headers
  return typeof value === "string" ? value : JSON.stringify(value, null, 2)
}

/**
 * Get custom payload as string (for editing)
 */
export const getCustomPayload = (metadata: Record<string, unknown>): string => {
  const liveKitData = metadata as LiveKitMetadata
  if (!liveKitData.token_request_payload) {
    return ""
  }
  // Return as-is if string, stringify if object
  const value = liveKitData.token_request_payload
  return typeof value === "string" ? value : JSON.stringify(value, null, 2)
}
